import React, { useEffect } from "react";
import { Dialog, DialogContent, Grid } from "@mui/material";
import { Box, Card, Chip, DateTimeStandard, Typography } from '@curves/ui-base';

import { GradientColorHeader } from "@Local/Components/Dialog/";
import { OverviewListLayout } from "@Local/Components/OverviewList";
import { formatCurrencyFn } from "@Util/utils";
import { parseDate } from "@Util/moment.util";
import { matchOrderPaymentStatusColors } from "@Local/CyclePurchase/staticData";
import useOrderCyclePurchaseApi from "@Apis/useOrderCyclePurchaseApi";
import useOverview from "@Util/hook/useOverview";
/**
 * 會員收費明細對話框組件
 *
 * 遵循標準 Dialog PROP 格式：
 * - open: 控制對話框開關
 * - onClose: 關閉視窗方法
 * - sourceData: 彈窗需要的資料（包含 memberID 及可選的會員資訊：clubName, memberName, cellPhone, memberNo）
 * - refresh: 成功操作後更新的方法（共識方法）
 *
 * @component
 * @param {Object} props - 組件屬性
 * @param {boolean} props.open - 控制對話框開關
 * @param {Function} props.onClose - 關閉視窗方法
 * @param {Object} props.sourceData - 彈窗需要的資料（包含 memberID 及可選的會員資訊）
 * @param {Function} props.refresh - 成功操作後更新的方法
 * @returns {JSX.Element} 會員收費明細對話框組件
 */
const MemberOrderDetailsDialog = props => {
	const { open, onClose, sourceData, refresh } = props;
	const { cycleOrderID, memberID, clubName, memberName, cellPhone, memberNo } = sourceData || {};
	const { postMembersCyclePurchaseOrdersApi } = useOrderCyclePurchaseApi();
	// 使用 useOverview 管理列表和分頁狀態
	const { overviewData, getOverviewData, updateOverviewPage } = useOverview({
		getOverviewApi: params => postMembersCyclePurchaseOrdersApi(memberID, cycleOrderID, params),
		defaultSearchParams: {
			pageIndex: 1,
			pageSize: 10,
		},
		isFetchOnMount: false,
		enableQueryString: false,
	});
	const ordersData = overviewData?.list;
	const pagination = overviewData?.pagination;
	// 會員收費明細
	const headerRow = () => {
		return [
			{
				cellKey: "orderDate",
				headerLabel: "日期",
				width: "15",
				align: "center",
				formatCell: (cellValue, rowData) => {
					return <Typography>{parseDate(rowData?.orderDate, DateTimeStandard.DisplayDate)}</Typography>;
				},
			},
			{
				cellKey: "itemName",
				headerLabel: "品項",
				width: "25",
				formatCell: (cellValue, rowData) => {
					return <Typography>{rowData?.itemName || "-"}</Typography>;
				},
			},
			{
				cellKey: "paymentStatus",
				headerLabel: "付款狀態",
				width: "15",
				align: "center",
				formatCell: (cellValue, rowData) => {
					return <Chip label={rowData?.paymentStatusText || "-"} color={matchOrderPaymentStatusColors(rowData?.paymentStatus || 0)} title={"付款狀態"} />;
				},
			},
			{
				cellKey: "itemPrice",
				headerLabel: "金額",
				width: "15",
				align: "right",
				formatCell: (cellValue, rowData) => {
					return <Typography>{formatCurrencyFn(rowData?.itemPrice || 0)}</Typography>;
				},
			},
			{
				cellKey: "notes",
				headerLabel: "備註",
				width: "15",
				align: "center",
				formatCell: (cellValue, rowData) => {
					return <Typography>{rowData?.notes || "-"}</Typography>;
				},
			},
		];
	};
	// 當對話框開啟且有 memberID 時，取得資料
	useEffect(() => {
		if (open && memberID) {
			// 每次開啟時重置到第一頁
			getOverviewData({ pageIndex: 1, pageSize: 10 });
		}
		// eslint-disable-next-line
	}, [open, memberID]);
	const handlePageChange = (e, newPageIndex) => {
		updateOverviewPage(newPageIndex);
	};
	return (
		<Dialog open={open} maxWidth="lg" fullWidth>
			<GradientColorHeader onClose={onClose} refresh={refresh}>
				循環訂單收費明細
			</GradientColorHeader>
			<DialogContent>
				<Box className="mb-3">
					<Card className="px-4 py-3" variant="elevation" elevation={1}>
						<Card.CardContent>
							<Grid spacing={2} container>
								<Grid xs={3} item>
									<Grid container>
										<Grid xs={5} item>
											<Box className="font-color-3">店鋪名</Box>
										</Grid>
										<Grid xs={7} item>
											<Box>{clubName || "-"}</Box>
										</Grid>
									</Grid>
								</Grid>
								<Grid xs={3} item>
									<Grid container>
										<Grid xs={5} item>
											<Box className="font-color-3">會員編號</Box>
										</Grid>
										<Grid xs={7} item>
											<Box>{memberNo || "-"}</Box>
										</Grid>
									</Grid>
								</Grid>
								<Grid xs={3} item>
									<Grid container>
										<Grid xs={5} item>
											<Box className="font-color-3">會員</Box>
										</Grid>
										<Grid xs={7} item>
											<Box>{memberName || "-"}</Box>
										</Grid>
									</Grid>
								</Grid>
								<Grid xs={3} item>
									<Grid container>
										<Grid xs={5} item>
											<Box className="font-color-3">手機</Box>
										</Grid>
										<Grid xs={7} item>
											<Box>{cellPhone || "-"}</Box>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Card.CardContent>
					</Card>
				</Box>
				<OverviewListLayout>
					<OverviewListLayout.Table
						headerRow={headerRow()}
						list={ordersData}
						isPagination={true}
						totalPage={pagination.totalPage}
						pageIndex={pagination.pageIndex}
						onChangePage={handlePageChange}
					/>
				</OverviewListLayout>
			</DialogContent>
		</Dialog>
	);
};
export default MemberOrderDetailsDialog;
